/**
 * Guday — Event Layer Tracking
 * Versão: 1.2
 *
 * Dispara eventos dataLayer para GTM/GA4 com base em atributos data-tracking.
 * Funciona com qualquer seção que tenha data-tracking="nome_do_evento".
 * Inclui tracking do cart drawer (Kaching) via event delegation.
 *
 * Arquitetura:
 *   Elemento HTML (data-tracking="evento")
 *     → tracking.js → dataLayer.push({ event, section, click_text, click_url, click_element })
 *       → GTM (escuta dataLayer)
 *         → GA4 (recebe o evento)
 */

(function () {
  "use strict";

  window.dataLayer = window.dataLayer || [];

  // ─── Config ──────────────────────────────────────────────────────────────────
  var DEBOUNCE_MS = 300;
  var MAX_TEXT_LENGTH = 100;

  // Mapa de nomes de evento — se o tracking name tem um evento customizado, usa ele.
  // Caso contrário, usa o próprio tracking name como evento.
  var EVENT_MAP = {
    menu: "menu_click",
    footer: "footer_click"
  };

  // Parâmetros extras por seção
  var EXTRA_PARAMS = {
    menu: { menu_location: "header" },
    footer: { footer_location: "bottom" }
  };

  // ─── Utilitários ─────────────────────────────────────────────────────────────

  /**
   * Limpa e trunca texto para envio ao dataLayer.
   */
  function cleanText(str) {
    if (!str) return "";
    return str.replace(/\s+/g, " ").trim().substring(0, MAX_TEXT_LENGTH);
  }

  /**
   * Debounce simples — evita disparos duplicados em cliques rápidos.
   */
  var lastPush = 0;
  function shouldPush() {
    var now = Date.now();
    if (now - lastPush < DEBOUNCE_MS) return false;
    lastPush = now;
    return true;
  }

  /**
   * Encontra o elemento clicável mais próximo (a, button, [role=button], input[type=submit]).
   */
  function findClickable(target) {
    return target.closest("a, button, [role='button'], input[type='submit']");
  }

  // ─── Tracking automático ─────────────────────────────────────────────────────

  /**
   * Registra listeners de clique em TODAS as seções com data-tracking.
   * Abordagem genérica: qualquer seção com o atributo é automaticamente rastreada.
   */
  function initTracking() {
    var sections = document.querySelectorAll("[data-tracking]");

    sections.forEach(function (section) {
      section.addEventListener("click", function (e) {
        if (!shouldPush()) return;

        var trackingName = section.getAttribute("data-tracking");
        if (!trackingName) return;

        var clickable = findClickable(e.target);
        var target = clickable || e.target;

        var eventName = EVENT_MAP[trackingName] || trackingName;

        // Se o elemento tem data-tracking-product, usa como click_text (ex: product cards)
        var productName = section.getAttribute("data-tracking-product");
        var clickText = cleanText(
          productName || target.innerText || target.getAttribute("aria-label") || ""
        );
        var clickUrl = target.href || target.closest("a") ? (target.closest("a") || {}).href || "" : "";

        var payload = {
          event: eventName,
          section: trackingName,
          click_text: clickText,
          click_url: clickUrl,
          click_element: target.tagName.toLowerCase()
        };

        // Se o elemento ou um ancestral tem data-tracking-position, adiciona ao payload
        // Busca primeiro no elemento clicado/ancestral, depois no container da seção
        var positionEl = e.target.closest("[data-tracking-position]");
        var position = positionEl ? positionEl.getAttribute("data-tracking-position") : section.getAttribute("data-tracking-position");

        // Detecção de posição para reviews Okendo (product_avaliacoes)
        if (!position && trackingName === "product_avaliacoes") {
          var reviewCard = e.target.closest("[data-oke-review-id], .oke-reviewContent, .okeReview");
          if (reviewCard && reviewCard.parentElement) {
            var reviewSiblings = reviewCard.parentElement.querySelectorAll("[data-oke-review-id], .oke-reviewContent, .okeReview");
            for (var ri = 0; ri < reviewSiblings.length; ri++) {
              if (reviewSiblings[ri] === reviewCard) { position = String(ri + 1); break; }
            }
          }
        }

        if (position) {
          payload.click_position = parseInt(position, 10);
        }

        // Adiciona parâmetros extras se configurados
        var extras = EXTRA_PARAMS[trackingName];
        if (extras) {
          for (var key in extras) {
            if (extras.hasOwnProperty(key)) {
              payload[key] = extras[key];
            }
          }
        }

        // Usa eventCallback do GTM para garantir envio antes de navegar
        // Sem bloquear a navegação — zero impacto em performance/conversão
        var link = e.target.closest("a[href]");
        var isNavigating = link && link.href && link.target !== "_blank" && !link.href.startsWith("javascript");

        if (isNavigating) {
          e.preventDefault();
          payload.eventCallback = function () {
            window.location.href = link.href;
          };
          // Timeout de segurança — navega mesmo se o GTM falhar
          payload.eventTimeout = 500;
        }

        window.dataLayer.push(payload);
      });
    });
  }

  // ─── Tolstoy UGC — fallback para Shadow DOM / Web Components ────────────────

  /**
   * O Tolstoy carousel renderiza como <tolstoy-carousel> (web component).
   * Se usar Shadow DOM, cliques internos não propagam para o data-tracking pai.
   * Este fallback observa o DOM e adiciona um listener direto no componente.
   */
  /**
   * Detecta a posição do item clicado dentro do Tolstoy carousel.
   * Procura o elemento de vídeo/slide mais próximo e calcula seu índice
   * entre os siblings do mesmo tipo.
   */
  function getTolstoyPosition(target) {
    var tile = target.closest(".tolstoy-carousel-tile-container");
    if (tile && tile.parentElement) {
      var siblings = tile.parentElement.querySelectorAll(".tolstoy-carousel-tile-container");
      for (var i = 0; i < siblings.length; i++) {
        if (siblings[i] === tile) return i + 1;
      }
    }
    return null;
  }

  function attachTolstoyListener(carousel) {
    // Detecta o contexto via data-tracking do elemento pai (ex: product_ugc, home_ugc)
    var trackingParent = carousel.closest("[data-tracking]");
    var trackingName = trackingParent ? trackingParent.getAttribute("data-tracking") : "home_ugc";

    carousel.addEventListener("click", function (e) {
      if (!shouldPush()) return;
      var position = getTolstoyPosition(e.target);
      var payload = {
        event: trackingName,
        section: trackingName,
        click_text: "tolstoy_video",
        click_url: "",
        click_element: "tolstoy-carousel"
      };
      if (position) {
        payload.click_position = position;
      }
      window.dataLayer.push(payload);
    });
  }

  function initTolstoyTracking() {
    var carousels = document.querySelectorAll("tolstoy-carousel");
    carousels.forEach(attachTolstoyListener);

    // Se o Tolstoy carrega depois, observar o DOM
    if (carousels.length === 0 && typeof MutationObserver !== "undefined") {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeName && node.nodeName.toLowerCase() === "tolstoy-carousel") {
              attachTolstoyListener(node);
              observer.disconnect();
            }
          });
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  // ─── CTA Comprar (Kaching Bundles) — tracking do botão de compra ────────────

  /**
   * O Kaching Bundles substitui o botão nativo de compra por seu próprio botão.
   * Detectamos cliques nos botões do Kaching e no botão nativo product-form__submit.
   */
  function initCtaTracking() {
    // Usa event delegation no document para capturar qualquer botão de compra
    // independente de quando o Kaching renderiza
    document.addEventListener("click", function (e) {
      var btn = e.target.closest(".quantity-bundle__buy-button, .kaching-bundles__add-to-cart, .product-form__submit, [data-tracking='product_cta'] button, button.button--primary[type='submit'][name='add']");
      if (!btn) return;

      // Não usar debounce global — CTA sempre dispara
      window.dataLayer.push({
        event: "product_cta",
        section: "product_cta",
        click_text: cleanText(btn.innerText),
        click_url: "",
        click_element: btn.tagName.toLowerCase()
      });
    });
  }

  // ─── Cart Drawer (Kaching) — tracking via event delegation ─────────────────

  /**
   * O cart drawer é renderizado pelo app Kaching (<kaching-cart-drawer>).
   * Não é possível adicionar data-tracking no Liquid, então usamos
   * event delegation com mapa de seletores CSS → eventos GA4.
   */

  var CART_SELECTORS = [
    { selector: ".kaching-cart__close",                              event: "cart_fechar" },
    { selector: ".kaching-cart__tiered-promotions-bar",              event: "cart_barra_de_progresso" },
    { selector: ".kaching-cart-item__quantity-btn--increment",       event: "cart_qtd_aumentar" },
    { selector: ".kaching-cart-item__quantity-input",                event: "cart_qtd_alterar" },
    { selector: ".kaching-cart-item__quantity-btn--decrement",       event: "cart_qtd_diminuir" },
    { selector: ".kaching-cart-item__remove-btn",                    event: "cart_remover_produto" },
    { selector: ".kaching-cart__discount-code-input",                event: "cart_cupom_campo" },
    { selector: ".kaching-cart__discount-code-apply",                event: "cart_cupom_aplicar" },
    { selector: "#kaching-cart__checkout-button",                    event: "cart_cta_checkout" },
    { selector: ".kaching-cart__payment-icons",                      event: "cart_payments" },
    { selector: ".kaching-cart__continue-shopping",                  event: "cart_continuar_comprando" }
  ];

  /**
   * Extrai o nome do produto do cart item mais próximo ao elemento clicado.
   */
  function getCartProductName(el) {
    var item = el.closest(".kaching-cart-item");
    if (!item) return "";
    var title = item.querySelector(".kaching-cart-item__title");
    return title ? cleanText(title.innerText) : "";
  }

  /**
   * Encontra qual evento do cart corresponde ao elemento clicado.
   * Verifica se o target (ou ancestral) corresponde a algum seletor do mapa.
   */
  function matchCartSelector(target, drawer) {
    for (var i = 0; i < CART_SELECTORS.length; i++) {
      var entry = CART_SELECTORS[i];
      var matched = target.closest(entry.selector);
      if (matched && drawer.contains(matched)) {
        return { event: entry.event, matched: matched };
      }
    }
    return null;
  }

  function attachCartListeners(drawer) {
    if (drawer.dataset.trackingBound) return;
    drawer.dataset.trackingBound = "true";

    drawer.addEventListener("click", function (e) {
      if (!shouldPush()) return;

      var result = matchCartSelector(e.target, drawer);
      if (!result) return;

      var eventName = result.event;
      var matched = result.matched;
      var clickText = "";

      // Contexto específico por evento
      if (eventName === "cart_qtd_aumentar" || eventName === "cart_qtd_diminuir" || eventName === "cart_remover_produto") {
        clickText = getCartProductName(matched);
      } else if (eventName === "cart_cupom_aplicar") {
        var input = drawer.querySelector(".kaching-cart__discount-code-input");
        clickText = input ? cleanText(input.value) : "";
      } else if (eventName === "cart_cta_checkout") {
        clickText = cleanText(matched.innerText);
      } else if (eventName === "cart_payments") {
        var icon = e.target.closest("img");
        clickText = icon ? (icon.alt || "") : "payment_icons";
      } else {
        clickText = cleanText(matched.innerText || matched.getAttribute("aria-label") || "");
      }

      window.dataLayer.push({
        event: eventName,
        section: "cart_drawer",
        click_text: clickText,
        click_url: "",
        click_element: e.target.tagName.toLowerCase()
      });
    });

    // Tracking de foco no campo de cupom (não é clique, mas interação relevante)
    var couponInput = drawer.querySelector(".kaching-cart__discount-code-input");
    if (couponInput) {
      couponInput.addEventListener("focus", function () {
        if (!shouldPush()) return;
        window.dataLayer.push({
          event: "cart_cupom_campo",
          section: "cart_drawer",
          click_text: "",
          click_url: "",
          click_element: "input"
        });
      });
    }

    // Tracking de abertura do drawer — observa o atributo "open"
    if (typeof MutationObserver !== "undefined") {
      var openObserver = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName === "open" && drawer.getAttribute("open") === "true") {
            window.dataLayer.push({
              event: "cart_abrir",
              section: "cart_drawer",
              click_text: "",
              click_url: "",
              click_element: "kaching-cart-drawer"
            });
            break;
          }
        }
      });
      openObserver.observe(drawer, { attributes: true, attributeFilter: ["open"] });
    }
  }

  function initCartTracking() {
    var drawer = document.querySelector("kaching-cart-drawer");
    if (drawer) {
      attachCartListeners(drawer);
      return;
    }

    // Kaching carrega dinamicamente — observar o DOM
    if (typeof MutationObserver !== "undefined") {
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var nodes = mutations[i].addedNodes;
          for (var j = 0; j < nodes.length; j++) {
            var node = nodes[j];
            if (node.nodeType === 1) {
              var target = node.nodeName.toLowerCase() === "kaching-cart-drawer"
                ? node
                : node.querySelector && node.querySelector("kaching-cart-drawer");
              if (target) {
                attachCartListeners(target);
                observer.disconnect();
                return;
              }
            }
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  // ─── Inicialização ───────────────────────────────────────────────────────────

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initTracking();
      initTolstoyTracking();
      initCtaTracking();
      initCartTracking();
    });
  } else {
    initTracking();
    initTolstoyTracking();
    initCtaTracking();
    initCartTracking();
  }
})();
